import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import client from '../../api/client';

interface PresentUser {
  user_id: string;
  full_name: string | null;
  email: string;
}

interface PresenceIndicatorProps {
  /** Stable string identifying the page/entity. The backend keys
   * presence on (org, scope_key). Examples:
   *   "company:<id>"
   *   "tree_node:<company>:<code>"
   *   "page:/cross-audits"
   */
  scopeKey: string;
}

function initials(name: string | null, email: string): string {
  const src = (name || email).trim();
  const parts = src.split(/\s+/);
  const a = (parts[0] || '?').charAt(0);
  const b = (parts[1] || '').charAt(0);
  return (a + b).toUpperCase();
}

function colorFor(seed: string): string {
  const palette = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

/** Shows compact avatars of other users currently active at the same
 * scope. Sends a heartbeat every 25s while mounted, queries who else
 * is here every 15s. */
export default function PresenceIndicator({ scopeKey }: PresenceIndicatorProps) {
  useEffect(() => {
    const beat = () => {
      client.post('/presence/heartbeat', { scope_key: scopeKey }).catch(() => { /* silent */ });
    };
    beat();
    const id = window.setInterval(beat, 25_000);
    return () => window.clearInterval(id);
  }, [scopeKey]);

  const { data } = useQuery<PresentUser[]>({
    queryKey: ['presence', scopeKey],
    queryFn: async () => {
      const r = await client.get<PresentUser[]>('/presence/who', { params: { scope_key: scopeKey } });
      return r.data;
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  if (!data || data.length === 0) return null;
  const top = data.slice(0, 5);
  const overflow = data.length - top.length;

  return (
    <Stack direction="row" spacing={-0.75} alignItems="center">
      {top.map((u) => (
        <Tooltip
          key={u.user_id}
          title={`${u.full_name ?? u.email} צופה כאן עכשיו`}
          arrow
        >
          <Avatar
            sx={{
              width: 28, height: 28,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: colorFor(u.email),
              border: '2px solid',
              borderColor: 'background.paper',
            }}
          >
            {initials(u.full_name, u.email)}
          </Avatar>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <Tooltip title={`עוד ${overflow} משתמשים`} arrow>
          <Avatar
            sx={{
              width: 28, height: 28,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: 'text.secondary',
              border: '2px solid',
              borderColor: 'background.paper',
            }}
          >
            +{overflow}
          </Avatar>
        </Tooltip>
      )}
    </Stack>
  );
}
